import { getPageLanguage, hideElement, showElement } from "../utils";
import { Errors } from "../apps/types";
import { sanitizeHtml } from "./_sanitize-html";

export function showInputErrors(input: HTMLElement, errors: string[]): void {
  const formBlock = input.closest(".form-block");

  if (formBlock instanceof HTMLElement) {
    showFormBlockErrors(formBlock, errors);
  } else if (input instanceof HTMLInputElement && input.type === "checkbox") {
    const label = input.closest(".form-checkbox");

    if (label instanceof HTMLElement) {
      label.append(createHelpBlock(errors));
    } else {
      input.after(createHelpBlock(errors));
    }
  } else {
    input.after(createHelpBlock(errors));
  }
}

export function showFormBlockErrors(formBlock: HTMLElement, errors: string[]) {
  formBlock.classList.add("has-error");

  const helpBlock = formBlock.querySelector<HTMLElement>(".help-block");

  if (helpBlock) {
    helpBlock.replaceWith(createHelpBlock(errors));
  } else {
    formBlock.append(createHelpBlock(errors));
  }
}

export function clearInputErrors(input: HTMLElement): void {
  const formBlock = input.closest(".form-block.has-error");

  if (formBlock instanceof HTMLElement) {
    clearFormBlockErrors(formBlock);
  } else if (input instanceof HTMLInputElement && input.type === "checkbox") {
    input.closest(".form-checkbox")?.querySelector(".help-block")?.remove();
  }
}

export function clearFormBlockErrors(formBlock: HTMLElement) {
  formBlock.classList.remove("has-error");

  const helpBlocks = formBlock.querySelectorAll<HTMLElement>(".help-block");
  for (const helpBlock of helpBlocks) {
    helpBlock.remove();
  }
}

export function createHelpBlock(inputErrors: string[]): HTMLDivElement {
  const helpBlock = document.createElement("div");

  helpBlock.classList.add("help-block");

  for (const error of inputErrors) {
    const errorParagraph = document.createElement("p");
    errorParagraph.textContent = error;

    helpBlock.append(errorParagraph);
  }

  return helpBlock;
}

export function showFormInputsErrors(form: HTMLFormElement, errors: Errors) {
  for (const [inputId, inputErrors] of Object.entries(errors)) {
    const formBlock = form.querySelector(`[name="${inputId}"]`)
      ?.closest(".form-block");

    if (formBlock instanceof HTMLElement) {
      showFormBlockErrors(formBlock, inputErrors);
    }
  }
}

export function clearFormInputsErrors(form: HTMLFormElement) {
  const errorFormBlocks = getErrorFormBlocks(form);

  for (const formBlock of errorFormBlocks) {
    clearFormBlockErrors(formBlock);
  }
}

export function updateFormInputsErrors(form: HTMLFormElement, errors: Errors): void {
  clearFormInputsErrors(form);
  showFormInputsErrors(form, errors);
}

export function getFormSubmitButton(form: HTMLFormElement): HTMLButtonElement | null {
  return form.querySelector("button[type='submit']");
}

export function getErrorFormBlocks(form: HTMLFormElement): HTMLElement[] {
  return [...form.querySelectorAll<HTMLElement>(".form-block.has-error")];
}

export function getFormAction(form: HTMLFormElement): string | null {
  const action = form.getAttribute("action");
  return action && action.trim() !== "" ? action : null;
}

export function showFormError(form: HTMLFormElement, isHttpError: boolean = false) {
  let formError = form.querySelector<HTMLElement>(".js-form-error");
  if (!formError) {
    formError = createFormErrorElement();
    form.appendChild(formError);
  }

  const content = formError.querySelector<HTMLElement>(".alert-content");
  if (content) {
    const customMessage = isHttpError ? form.dataset.httpErrorMessage : undefined;

    content.innerHTML = customMessage
      ? sanitizeHtml(customMessage)
      : getFormErrorMessage();
  }

  showElement(formError);
}

export function hideFormError(form: HTMLFormElement) {
  const formError = form.querySelector<HTMLElement>(".js-form-error");
  if (formError) {
    hideElement(formError);
  }
}

export function getFormErrorMessage(): string {
  return getPageLanguage() === "ru"
    ? "Не удалось отправить данные. Попробуйте ещё раз."
    : "Failed to submit data. Please try again."
}

function createFormErrorElement(): HTMLDivElement {
  const wrapper = document.createElement("div");
  wrapper.className = "js-form-error form-error hidden";

  const alert = document.createElement("div");
  alert.className = "alert alert_warning alert_filled alert_size_s form-error__alert";

  const content = document.createElement("div");
  content.className = "alert-content";
  content.textContent = getFormErrorMessage();

  alert.appendChild(content);
  wrapper.appendChild(alert);

  return wrapper;
}

export function resetForm(form: HTMLFormElement): void {
  form.reset();

  form.querySelectorAll<HTMLSelectElement>("select").forEach((select) => {
    select.tomselect?.sync();
  });
}